import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  assignTicket,
  getTickets,
  setTicketState
} from '../../../../shared/services/data/ticket.data';
import { isErrorInfo } from '../../../../shared/interfaces/error-info.interface';
import { TicketPriority, TicketSlaState, TicketView } from '../../../../shared/models/ticket.model';
import { TicketUiModel } from '../../../../shared/ui-models/ticket.ui-model';
import { APP_ROUTES } from '../../../../shared/config/routes-config';
import { RouteConfig } from '../../../../shared/interfaces/routes-config.interface';
import { birdToast } from '../../../../shared/components/core-ui/toast';
import { cn } from '../../../../utils/cn';
import { TabBar } from './tab-bar.component';
import { BUTTON, BUTTON_DARK, CARD, CHIP, duration } from './dashboard-ui';

type Tab = Extract<TicketView, 'mine' | 'unassigned' | 'team'>;

const TABS: { key: Tab; label: string }[] = [
  { key: 'mine', label: 'Mine' },
  { key: 'unassigned', label: 'Unassigned' },
  { key: 'team', label: 'Team' }
];

const PRIORITY_STYLE: Record<TicketPriority, string> = {
  P1: 'bg-secondary-100 text-secondary-800',
  P2: 'bg-creams-600 text-creams-900',
  P3: 'bg-blue text-neutral-500',
  P4: 'bg-neutral-50 text-neutral-400'
};

const SLA_STYLE: Record<TicketSlaState, string> = {
  breached: 'bg-secondary-500 text-white',
  at_risk: 'bg-creams-600 text-creams-900',
  on_track: 'bg-primary-50 text-primary-700',
  met: 'bg-neutral-50 text-neutral-400',
  none: 'bg-neutral-50 text-neutral-300'
};

const byNextDue = (a: TicketUiModel, b: TicketUiModel) =>
  (a.sla.nextDue?.getTime() ?? Infinity) - (b.sla.nextDue?.getTime() ?? Infinity);

const slaLabel = (ticket: TicketUiModel, now: number) => {
  if (!ticket.sla.nextDue) return ticket.sla.state === 'none' ? 'No SLA' : 'Met';
  const ms = ticket.sla.nextDue.getTime() - now;
  return ms >= 0 ? `in ${duration(ms)}` : `${duration(ms)} late`;
};

const detailsPath = (id: number) =>
  (APP_ROUTES.TICKETS_MODULE.CHILDREN as RouteConfig).DETAILS.FULL_PATH.replace(
    ':ticketId',
    String(id)
  );

export const TicketsWidget = () => {
  const history = useHistory();
  const [tab, setTab] = useState<Tab>('mine');
  const [lists, setLists] = useState<Partial<Record<Tab, TicketUiModel[]>>>({});
  const [failed, setFailed] = useState(false);
  const [busyId, setBusyId] = useState<number>();

  const load = useCallback(() => {
    Promise.all(TABS.map((t) => getTickets(t.key))).then((results) => {
      if (results.some(isErrorInfo)) {
        setFailed(true);
        return;
      }
      setFailed(false);
      setLists(
        TABS.reduce(
          (all, t, i) => ({
            ...all,
            [t.key]: [...(results[i] as TicketUiModel[])].sort(byNextDue)
          }),
          {}
        )
      );
    });
  }, []);

  useEffect(load, [load]);

  const act = (ticket: TicketUiModel, action: 'take' | 'resolve') => {
    setBusyId(ticket.id);
    const request =
      action === 'take' ? assignTicket(ticket.id, 'me') : setTicketState(ticket.id, 'closed');
    request.then((result) => {
      setBusyId(undefined);
      if (isErrorInfo(result)) {
        birdToast.error(
          action === 'take' ? 'Could not take the ticket' : 'Could not resolve the ticket'
        );
        return;
      }
      birdToast.success(
        action === 'take' ? `#${ticket.number} is yours` : `#${ticket.number} resolved`
      );
      load();
    });
  };

  const now = Date.now();
  const tickets = lists[tab];

  return (
    <section className={CARD}>
      <h2 className="m-0 px-5 pt-5 text-[18px] font-gilroy-bold text-neutral-500">Tickets</h2>
      <div className="mt-2">
        <TabBar
          tabs={TABS.map((t) => ({ ...t, count: lists[t.key]?.length }))}
          active={tab}
          onChange={setTab}
        />
      </div>
      {failed ? (
        <p className="px-5 py-12 text-center text-[14px] text-neutral-300">
          Tickets could not be loaded.
        </p>
      ) : !tickets ? (
        <div className="space-y-3 px-5 py-5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="block h-10 animate-pulse rounded bg-gray-200 motion-reduce:animate-none"
            />
          ))}
        </div>
      ) : !tickets.length ? (
        <p className="px-5 py-12 text-center text-[14px] text-neutral-300">Nothing here</p>
      ) : (
        <ul className="m-0 list-none p-0">
          {tickets.map((ticket) => (
            <li
              key={ticket.id}
              onClick={() => history.push(detailsPath(ticket.id))}
              className="flex cursor-pointer flex-col gap-2.5 border-t border-gray-200 px-5 py-3.5 transition-colors first:border-t-0 hover:bg-creams-100 md:flex-row md:items-center md:gap-4"
            >
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <span className={cn(CHIP, PRIORITY_STYLE[ticket.priority])}>{ticket.priority}</span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-[14px] font-gilroy-bold text-neutral-500">
                      {ticket.title}
                    </span>
                    {ticket.emergency && (
                      <span className={cn(CHIP, 'bg-secondary-500 text-white')}>Emergency</span>
                    )}
                  </div>
                  <div className="mt-0.5 truncate text-[12px] tabular-nums text-neutral-300">
                    {[
                      `#${ticket.number}`,
                      ticket.categoryLabel,
                      ticket.unitRef,
                      ticket.reservationRef
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 pl-10 md:pl-0">
                {tab === 'team' && ticket.ownerName && (
                  <span className="text-[12px] text-neutral-300">{ticket.ownerName}</span>
                )}
                <span className={cn(CHIP, 'tabular-nums', SLA_STYLE[ticket.sla.state])}>
                  {slaLabel(ticket, now)}
                </span>
                {tab === 'unassigned' && (
                  <button
                    type="button"
                    disabled={busyId === ticket.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      act(ticket, 'take');
                    }}
                    className={BUTTON_DARK}
                  >
                    Take
                  </button>
                )}
                {tab === 'mine' && (
                  <button
                    type="button"
                    disabled={busyId === ticket.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      act(ticket, 'resolve');
                    }}
                    className={BUTTON}
                  >
                    Resolve
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};
